// components/CFOIssues/CFOIssuesPage.jsx — CFO Bi-weekly Issue List (editor + admin only)
import React, { useState, useMemo } from "react";
import {
  Avatar, Button, Field, Select, Spinner, Text, Textarea, Tooltip,
} from "@fluentui/react-components";
import {
  Flag24Regular, Add24Regular, Dismiss24Regular,
  DocumentArrowDown24Regular, ArrowSync24Regular,
  CheckmarkCircle24Regular, Warning24Regular,
  Clock24Regular, ArrowTrendingLines24Regular,
  Eye24Regular, Search24Regular,
} from "@fluentui/react-icons";
import { useActivityTracker }  from "../../hooks/useActivityTracker";
import { useUserAccess }       from "../../context/UserAccessContext";
import { generateCFOExcel }    from "../../services/reportService";
import { useMsal }             from "@azure/msal-react";
import { usePagination, Pagination, useDebounced } from "../common/Pagination";
import { latestProgressNote } from "../../services/activityService";

const STATUS_STYLE = {
  "Not Started": { bg: "#F3F2F1", color: "#605E5C" },
  "In Progress": { bg: "#EBF3FF", color: "#0052A3" },
  "On Hold":     { bg: "#FFF4EC", color: "#C4762A" },
  "Completed":   { bg: "#EAF6EA", color: "#217A21" },
  "Closed":      { bg: "#EBEBEB", color: "#444"    },
};
const PRIORITY_DOT = { High: "#C4262E", Medium: "#D1521A", Low: "#217A21" };
const CATEGORY_COLOR = {
  Procurement: "#0066FF", Logistics: "#006E6E", Contracts: "#7B2D8B",
  HSE: "#C4262E", Projects: "#D1521A", General: "#637074",
};

function fmtDate(d) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}
function daysLeft(due) {
  if (!due) return null;
  return Math.ceil((new Date(due) - new Date()) / 86400000);
}

// ── Stat card ──────────────────────────────────────────────────────────────────
function StatCard({ label, count, color, bg, icon: Icon }) {
  return (
    <div style={{ flex: 1, minWidth: 130, background: "#fff", border: `1.5px solid ${color}28`, borderRadius: 14, padding: "16px 18px", boxShadow: "0 1px 4px rgba(0,0,0,0.05)" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: color + "18", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Icon style={{ color, fontSize: 18 }} />
        </div>
        <span style={{ fontSize: 30, fontWeight: 800, color: "#001A3D", lineHeight: 1 }}>{count}</span>
      </div>
      <span style={{ fontSize: 10, fontWeight: 700, color: "#888", letterSpacing: "0.6px" }}>{label.toUpperCase()}</span>
    </div>
  );
}

// ── Recommendation timeline ────────────────────────────────────────────────────
function RecTimeline({ recs }) {
  if (!recs || recs.length === 0) {
    return <Text style={{ color: "#999", fontSize: 12, fontStyle: "italic" }}>No recommendations yet.</Text>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {recs.map((r, i) => (
        <div key={r.id} style={{ display: "flex", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
            <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#F0E0F8", border: "2px solid #7B2D8B", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <CheckmarkCircle24Regular style={{ color: "#7B2D8B", fontSize: 14 }} />
            </div>
            {i < recs.length - 1 && <div style={{ width: 2, flex: 1, background: "#E8D0F8", margin: "4px 0" }} />}
          </div>
          <div style={{ flex: 1, paddingBottom: 16 }}>
            <div style={{ fontSize: 11, color: "#999", marginBottom: 6 }}>
              {new Date(r.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}
              {" · "}{r.byName || r.by}
            </div>
            <div style={{ background: "#FAF0FF", border: "1px solid #E8D0F8", borderRadius: 8, padding: "10px 14px" }}>
              <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.6 }}>{r.text}</Text>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── CFO Issue detail panel ─────────────────────────────────────────────────────
function CFODetailPanel({ activity, onClose, onAddRecommendation, onToggleCFO, currentUserEmail, currentUserName, canEdit }) {
  const [showRecForm, setShowRecForm] = useState(false);
  const [recText, setRecText]         = useState("");
  const [savingRec, setSavingRec]     = useState(false);

  const recs     = activity.cfoRecommendations || [];
  const ss       = STATUS_STYLE[activity.status] || STATUS_STYLE["Not Started"];
  const dl       = daysLeft(activity.dueDate);
  const isOverdue = dl !== null && dl < 0 && !["Completed", "Closed"].includes(activity.status);
  const catColor  = CATEGORY_COLOR[activity.category] || "#637074";
  const keyAct    = latestProgressNote(activity);

  async function submitRec() {
    if (!recText.trim()) return;
    setSavingRec(true);
    try {
      await onAddRecommendation(activity.id, { text: recText.trim(), byEmail: currentUserEmail, byName: currentUserName });
      setRecText("");
      setShowRecForm(false);
    } finally {
      setSavingRec(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.25)", zIndex: 290, backdropFilter: "blur(2px)" }} />
      <div style={{
        position: "fixed", top: 0, right: 0, width: 540, height: "100vh",
        background: "#fff", zIndex: 300, boxShadow: "-4px 0 32px rgba(0,0,0,0.14)",
        display: "flex", flexDirection: "column",
      }}>
        {/* Header */}
        <div style={{ padding: "20px 24px 16px", borderBottom: "1px solid #EDEBE9", background: "#FAFBFF", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", gap: 6, marginBottom: 8, flexWrap: "wrap", alignItems: "center" }}>
                <span style={{ background: "#7B2D8B18", color: "#7B2D8B", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>CFO ISSUE</span>
                <span style={{ background: `${catColor}18`, color: catColor, padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{activity.category}</span>
                <span style={{ background: ss.bg, color: ss.color, padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 700 }}>{activity.status}</span>
                {activity.userDept && <span style={{ background: "#EEF1FF", color: "#3451B2", padding: "3px 10px", borderRadius: 20, fontSize: 10, fontWeight: 600 }}>{activity.userDept}</span>}
              </div>
              <Text size={500} weight="bold" style={{ color: "#001A3D", lineHeight: 1.3, display: "block" }}>{activity.title}</Text>
              {activity.contractor && <Text style={{ fontSize: 12, color: "#666", display: "block", marginTop: 4 }}>Vendor: <strong>{activity.contractor}</strong></Text>}
            </div>
            <Button appearance="subtle" icon={<Dismiss24Regular />} onClick={onClose} style={{ flexShrink: 0 }} />
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: "20px 24px", overflowY: "auto" }}>

          {/* Summary grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
            <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "12px 14px" }}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>DUE DATE</Text>
              <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>{fmtDate(activity.dueDate)}</Text>
              {dl !== null && (
                <Text style={{ fontSize: 12, color: isOverdue ? "#D13438" : dl <= 3 ? "#E65C00" : "#107C10", fontWeight: 600 }}>
                  {isOverdue ? `${Math.abs(dl)}d overdue` : dl === 0 ? "Due today" : `${dl}d remaining`}
                </Text>
              )}
            </div>
            <div style={{ background: "#F8F9FC", borderRadius: 10, padding: "12px 14px" }}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 4 }}>ASSIGNED TO</Text>
              <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>{activity.assignedToName || "—"}</Text>
              <Text style={{ fontSize: 11, color: "#888" }}>{activity.assignedTo || ""}</Text>
            </div>
          </div>

          {/* Owners */}
          {(() => {
            const owners = activity.owners?.length > 0
              ? activity.owners
              : (activity.ownerEmail ? [{ email: activity.ownerEmail, name: activity.ownerName, jobTitle: activity.ownerJobTitle }] : []);
            if (!owners.length) return null;
            return (
              <div style={{ marginBottom: 16 }}>
                <Text style={{ fontSize: 11, fontWeight: 700, color: "#888", letterSpacing: "0.4px", display: "block", marginBottom: 8 }}>
                  CONTRACT OWNER{owners.length > 1 ? "S" : ""}
                </Text>
                {owners.map((o, i) => (
                  <div key={o.email || i} style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "10px 14px", marginBottom: 6, display: "flex", alignItems: "center", gap: 10 }}>
                    <Avatar name={o.name || o.email} size={28} color="brand" />
                    <div style={{ flex: 1 }}>
                      <Text style={{ fontWeight: 700, color: "#001A3D", fontSize: 13, display: "block" }}>{o.name || o.email}</Text>
                      {o.jobTitle && <Text style={{ fontSize: 11, color: "#555", display: "block" }}>{o.jobTitle}</Text>}
                      <Text style={{ fontSize: 12, color: "#0066FF" }}>{o.email}</Text>
                    </div>
                  </div>
                ))}
              </div>
            );
          })()}

          {/* Key Activities */}
          {keyAct && (
            <div style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#0066FF", letterSpacing: "0.4px", display: "block", marginBottom: 6 }}>KEY ACTIVITIES</Text>
              <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.7 }}>{keyAct}</Text>
            </div>
          )}

          {/* Look Ahead */}
          {activity.lookAhead && (
            <div style={{ background: "#F5F8FF", border: "1px solid #D6E8FF", borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
              <Text style={{ fontSize: 11, fontWeight: 700, color: "#0066FF", letterSpacing: "0.4px", display: "block", marginBottom: 6 }}>LOOK AHEAD</Text>
              <Text style={{ fontSize: 13, color: "#333", lineHeight: 1.7 }}>{activity.lookAhead}</Text>
            </div>
          )}

          {/* Recommendations section */}
          <div style={{ borderTop: "1px solid #E8D0F8", paddingTop: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
              <Text style={{ fontSize: 13, fontWeight: 700, color: "#7B2D8B", letterSpacing: "0.3px" }}>
                RECOMMENDATIONS
                <span style={{ marginLeft: 8, background: "#7B2D8B18", color: "#7B2D8B", borderRadius: 20, padding: "1px 8px", fontSize: 11 }}>
                  {recs.length}
                </span>
              </Text>
              {canEdit && !showRecForm && (
                <Button appearance="subtle" size="small" icon={<Add24Regular />} onClick={() => setShowRecForm(true)} style={{ color: "#7B2D8B" }}>
                  Add Recommendation
                </Button>
              )}
            </div>

            {showRecForm && (
              <div style={{ background: "#FAF0FF", border: "1px solid #E8D0F8", borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
                <Text style={{ fontSize: 12, fontWeight: 700, color: "#7B2D8B", display: "block", marginBottom: 8 }}>New Recommendation</Text>
                <Textarea
                  value={recText}
                  onChange={(_, d) => setRecText(d.value)}
                  placeholder="Enter your recommendation for the CFO regarding this issue..."
                  rows={4}
                />
                <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                  <Button
                    appearance="primary" size="small"
                    onClick={submitRec}
                    disabled={!recText.trim() || savingRec}
                    style={{ background: "#7B2D8B" }}
                  >
                    {savingRec ? <Spinner size="tiny" /> : "Save Recommendation"}
                  </Button>
                  <Button appearance="secondary" size="small" onClick={() => { setShowRecForm(false); setRecText(""); }} disabled={savingRec}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <RecTimeline recs={recs} />
          </div>
        </div>

        {/* Footer */}
        <div style={{ padding: "14px 24px", borderTop: "1px solid #EDEBE9", display: "flex", gap: 8, background: "#FAFBFF", flexShrink: 0 }}>
          {canEdit && (
            <Button
              appearance="subtle" size="small"
              icon={<Flag24Regular />}
              onClick={() => onToggleCFO(activity.id, false)}
              style={{ color: "#D13438" }}
            >
              Remove from CFO Issues
            </Button>
          )}
          <Button appearance="secondary" onClick={onClose} style={{ marginLeft: "auto" }}>Close</Button>
        </div>
      </div>
    </>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function CFOIssuesPage() {
  const { accounts }  = useMsal();
  const { activities, loading, isDemoMode, refresh, toggleCFOIssue, addCFORecommendation } = useActivityTracker();
  const { canEdit, isEditor, isAdmin, currentUserEmail, currentUserName } = useUserAccess();

  const currentUserEmailResolved = currentUserEmail || accounts[0]?.username || "";
  const currentUserNameResolved  = currentUserName  || accounts[0]?.name     || "SCM User";

  const [selectedIssue, setSelectedIssue]     = useState(null);
  const [filterDept, setFilterDept]           = useState("All");
  const [filterStatus, setFilterStatus]       = useState("All");
  const [filterPriority, setFilterPriority]   = useState("All");
  const [search, setSearch]                   = useState("");
  const [weekEnding, setWeekEnding]           = useState(new Date().toISOString().slice(0, 10));
  const [exporting, setExporting]             = useState(false);

  const cfoIssues = useMemo(() =>
    activities.filter(a => a.cfoIssue && a.status !== "Closed"),
    [activities]);

  const stats = useMemo(() => ({
    total:    cfoIssues.length,
    progress: cfoIssues.filter(a => a.status === "In Progress").length,
    overdue:  cfoIssues.filter(a => a.dueDate && new Date(a.dueDate) < new Date() && !["Completed", "Closed"].includes(a.status)).length,
    withRecs: cfoIssues.filter(a => (a.cfoRecommendations || []).length > 0).length,
  }), [cfoIssues]);

  const depts = useMemo(() =>
    [...new Set(cfoIssues.map(a => a.userDept).filter(Boolean))].sort(),
    [cfoIssues]);

  const debouncedSearch = useDebounced(search, 220);

  const filtered = useMemo(() => {
    return cfoIssues.filter(a => {
      if (filterDept     !== "All" && a.userDept  !== filterDept)     return false;
      if (filterStatus   !== "All" && a.status    !== filterStatus)   return false;
      if (filterPriority !== "All" && a.priority  !== filterPriority) return false;
      if (debouncedSearch.trim()) {
        const q = debouncedSearch.toLowerCase();
        return a.title?.toLowerCase().includes(q) ||
          a.contractor?.toLowerCase().includes(q) ||
          a.userDept?.toLowerCase().includes(q) ||
          a.assignedToName?.toLowerCase().includes(q);
      }
      return true;
    });
  }, [cfoIssues, filterDept, filterStatus, filterPriority, debouncedSearch]);

  const pager = usePagination(filtered, {
    defaultPageSize: 50,
    resetKey: `${filterDept}|${filterStatus}|${filterPriority}|${debouncedSearch}`,
  });

  async function handleToggleCFO(id, newValue) {
    const updated = await toggleCFOIssue(id, newValue);
    if (updated && selectedIssue?.id === id) {
      if (!newValue) setSelectedIssue(null);
      else setSelectedIssue(updated);
    }
  }

  async function handleAddRecommendation(id, rec) {
    const result = await addCFORecommendation(id, rec);
    if (result && selectedIssue?.id === id) setSelectedIssue(result.activity);
    return result;
  }

  function handleExport() {
    setExporting(true);
    try { generateCFOExcel(filtered, weekEnding); }
    finally { setTimeout(() => setExporting(false), 600); }
  }

  // Access guard — only editor/admin can view this page
  if (!isEditor && !isAdmin) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: 400, gap: 16 }}>
        <Warning24Regular style={{ fontSize: 48, color: "#D13438" }} />
        <Text size={500} weight="bold" style={{ color: "#001A3D" }}>Access Restricted</Text>
        <Text style={{ color: "#666" }}>The CFO Issues List is accessible to editors and administrators only.</Text>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1320, margin: "0 auto", padding: "0 2px" }}>

      {/* ── HEADER ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 12, flexWrap: "wrap" }}>
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: "#7B2D8B", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Flag24Regular style={{ color: "#fff", fontSize: 20 }} />
            </div>
            <div>
              <Text size={600} weight="bold" style={{ color: "#001A3D", lineHeight: 1.2, display: "block" }}>CFO Bi-weekly Issue List</Text>
              <Text style={{ fontSize: 12, color: "#888" }}>LekOil SCM — issues flagged for CFO attention</Text>
            </div>
          </div>
          {isDemoMode && <span style={{ fontSize: 11, fontWeight: 700, color: "#E65C00", background: "#FFF4EC", border: "1px solid #FFD6A8", padding: "2px 10px", borderRadius: 20 }}>Demo Mode</span>}
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <Text style={{ fontSize: 12, color: "#555", whiteSpace: "nowrap" }}>Period ending:</Text>
            <input
              type="date"
              value={weekEnding}
              onChange={e => setWeekEnding(e.target.value)}
              style={{ padding: "6px 10px", border: "1px solid #D0D7E5", borderRadius: 8, fontSize: 13, outline: "none" }}
            />
          </div>
          <Button
            appearance="primary"
            icon={exporting ? <Spinner size="tiny" /> : <DocumentArrowDown24Regular />}
            onClick={handleExport}
            disabled={exporting || filtered.length === 0}
            style={{ background: "#7B2D8B", whiteSpace: "nowrap" }}
          >
            Export Excel
          </Button>
          <Tooltip content="Refresh" relationship="label">
            <Button appearance="subtle" icon={<ArrowSync24Regular />} onClick={refresh} />
          </Tooltip>
        </div>
      </div>

      {/* ── STATS ── */}
      <div style={{ display: "flex", gap: 12, marginBottom: 20, flexWrap: "wrap" }}>
        <StatCard label="Total Issues"       count={stats.total}    color="#7B2D8B" bg="#FAF0FF" icon={Flag24Regular} />
        <StatCard label="In Progress"        count={stats.progress} color="#0052A3" bg="#EBF3FF" icon={ArrowTrendingLines24Regular} />
        <StatCard label="Overdue"            count={stats.overdue}  color="#C4262E" bg="#FDE7E9" icon={Warning24Regular} />
        <StatCard label="With Recommendations" count={stats.withRecs} color="#107C10" bg="#EAF6EA" icon={CheckmarkCircle24Regular} />
      </div>

      {/* ── FILTERS ── */}
      <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "14px 16px", marginBottom: 16, display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        {/* Search */}
        <div style={{ position: "relative", flex: 1, minWidth: 180 }}>
          <Search24Regular style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#999", fontSize: 16 }} />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search issues..."
            style={{ width: "100%", boxSizing: "border-box", padding: "8px 10px 8px 34px", border: "1px solid #E4E8EF", borderRadius: 8, fontSize: 13, outline: "none" }}
            onFocus={e => { e.target.style.borderColor = "#7B2D8B"; }}
            onBlur={e => { e.target.style.borderColor = "#E4E8EF"; }}
          />
        </div>

        <Field>
          <Select value={filterDept} onChange={(_, d) => setFilterDept(d.value)} style={{ minWidth: 140 }}>
            <option value="All">All Departments</option>
            {depts.map(d => <option key={d} value={d}>{d}</option>)}
          </Select>
        </Field>
        <Field>
          <Select value={filterStatus} onChange={(_, d) => setFilterStatus(d.value)} style={{ minWidth: 130 }}>
            <option value="All">All Statuses</option>
            {["Not Started", "In Progress", "On Hold", "Completed"].map(s => <option key={s} value={s}>{s}</option>)}
          </Select>
        </Field>
        <Field>
          <Select value={filterPriority} onChange={(_, d) => setFilterPriority(d.value)} style={{ minWidth: 120 }}>
            <option value="All">All Priorities</option>
            {["High", "Medium", "Low"].map(p => <option key={p} value={p}>{p}</option>)}
          </Select>
        </Field>

        {(filterDept !== "All" || filterStatus !== "All" || filterPriority !== "All" || search) && (
          <Button appearance="subtle" size="small" onClick={() => { setFilterDept("All"); setFilterStatus("All"); setFilterPriority("All"); setSearch(""); }} style={{ color: "#888", whiteSpace: "nowrap" }}>
            Clear filters
          </Button>
        )}
      </div>

      {/* ── TABLE ── */}
      {loading ? (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <Spinner size="large" label="Loading CFO issues..." />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", padding: "60px 40px", textAlign: "center" }}>
          <Flag24Regular style={{ fontSize: 48, color: "#D0D7E5", marginBottom: 12 }} />
          <Text size={500} weight="bold" style={{ color: "#001A3D", display: "block", marginBottom: 8 }}>No CFO Issues</Text>
          <Text style={{ color: "#888" }}>
            {cfoIssues.length === 0
              ? "No activities have been flagged for CFO attention yet. Open any activity and use the \"Flag for CFO Issues\" button."
              : "No issues match the current filters."}
          </Text>
        </div>
      ) : (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E4E8EF", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#001A3D" }}>
                  <th style={{ width: 4, padding: 0 }} />
                  <th style={{ padding: "12px 10px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", width: 38 }}>#</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 220 }}>ACTIVITY / CONTRACT</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 120 }}>DEPT / ENTITY</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "left", minWidth: 160 }}>KEY ACTIVITIES</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", minWidth: 100 }}>STATUS</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", minWidth: 90 }}>PRIORITY</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", minWidth: 90 }}>DUE DATE</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", minWidth: 80 }}>RECS</th>
                  <th style={{ padding: "12px 14px", color: "#C8D8FF", fontSize: 10, fontWeight: 700, letterSpacing: "0.6px", textAlign: "center", width: 70 }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {pager.pageItems.map((a, i) => {
                  const idx        = pager.from - 1 + i;
                  const ss         = STATUS_STYLE[a.status] || STATUS_STYLE["Not Started"];
                  const catColor   = CATEGORY_COLOR[a.category] || "#637074";
                  const dl         = daysLeft(a.dueDate);
                  const isOverdue  = dl !== null && dl < 0 && !["Completed", "Closed"].includes(a.status);
                  const latestNote = latestProgressNote(a);
                  const recCount   = (a.cfoRecommendations || []).length;
                  const [hov, setHov] = [false, () => {}]; // eslint-disable-line
                  const isSelected = selectedIssue?.id === a.id;

                  return (
                    <tr
                      key={a.id}
                      onClick={() => setSelectedIssue(a)}
                      style={{
                        borderBottom: "1px solid #EEF0F4",
                        background: isSelected ? "#FAF0FF" : (isOverdue ? "#FFF8F8" : "#fff"),
                        cursor: "pointer",
                        borderLeft: isSelected ? "3px solid #7B2D8B" : "3px solid transparent",
                        transition: "background 0.12s",
                      }}
                      onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = "#F9F5FF"; }}
                      onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = isOverdue ? "#FFF8F8" : "#fff"; }}
                    >
                      <td style={{ width: 4, padding: 0 }}><div style={{ width: 4, minHeight: 52, background: catColor }} /></td>
                      <td style={{ padding: "12px 10px", fontSize: 12, color: "#AAA", textAlign: "center" }}>{idx + 1}</td>
                      <td style={{ padding: "12px 14px", minWidth: 220, maxWidth: 280 }}>
                        <span style={{ fontWeight: 700, color: "#001A3D", fontSize: 12.5, display: "block", lineHeight: 1.4 }}>{a.title}</span>
                        {a.contractor && <span style={{ fontSize: 10.5, color: "#999", display: "block", marginTop: 1 }}>{a.contractor}</span>}
                      </td>
                      <td style={{ padding: "12px 14px" }}>
                        <span style={{ fontSize: 12, color: "#555" }}>{a.userDept || "—"}</span>
                        {a.entity && a.entity !== "N/A" && <span style={{ fontSize: 11, color: "#888", display: "block" }}>{a.entity}</span>}
                      </td>
                      <td style={{ padding: "12px 14px", maxWidth: 220 }}>
                        {latestNote
                          ? <span style={{ fontSize: 11.5, color: "#555", lineHeight: 1.5, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{latestNote}</span>
                          : <span style={{ color: "#CCC", fontSize: 12 }}>—</span>}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <span style={{ background: ss.bg, color: ss.color, padding: "3px 9px", borderRadius: 10, fontSize: 10.5, fontWeight: 700, whiteSpace: "nowrap" }}>{a.status}</span>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                          <span style={{ width: 7, height: 7, borderRadius: "50%", background: PRIORITY_DOT[a.priority] || "#888" }} />
                          <span style={{ fontSize: 11, color: "#555" }}>{a.priority}</span>
                        </div>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center", whiteSpace: "nowrap" }}>
                        <span style={{ fontSize: 11.5, color: isOverdue ? "#C4262E" : "#555", fontWeight: isOverdue ? 700 : 400 }}>
                          {isOverdue ? `⚠ ${Math.abs(dl)}d overdue` : fmtDate(a.dueDate)}
                        </span>
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        {recCount > 0 ? (
                          <span style={{ background: "#7B2D8B18", color: "#7B2D8B", borderRadius: 20, padding: "2px 10px", fontSize: 11, fontWeight: 700 }}>{recCount}</span>
                        ) : (
                          <span style={{ color: "#CCC", fontSize: 12 }}>—</span>
                        )}
                      </td>
                      <td style={{ padding: "12px 14px", textAlign: "center" }}>
                        <Tooltip content="View details & recommendations" relationship="label">
                          <Button
                            appearance="subtle" size="small"
                            icon={<Eye24Regular />}
                            onClick={e => { e.stopPropagation(); setSelectedIssue(a); }}
                            style={{ color: "#7B2D8B" }}
                          />
                        </Tooltip>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Table footer */}
          <Pagination {...pager} accent="#7B2D8B" noun="flagged issue" />
        </div>
      )}

      {/* ── DETAIL PANEL ── */}
      {selectedIssue && (
        <CFODetailPanel
          activity={selectedIssue}
          onClose={() => setSelectedIssue(null)}
          onAddRecommendation={handleAddRecommendation}
          onToggleCFO={handleToggleCFO}
          currentUserEmail={currentUserEmailResolved}
          currentUserName={currentUserNameResolved}
          canEdit={canEdit}
        />
      )}
    </div>
  );
}
